<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Suporte</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Consulte, filtre e acompanhe seus tickets de suporte.
          </p>
        </div>
      </div>

      <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="p-6">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-500">Status</label>

              <div class="relative group">
                <select
                  v-model="status"
                  id="status-select"
                  class="h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white px-4 pr-10 text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Todos</option>
                  <option value="Em aberto">Em aberto</option>
                  <option value="Concluído">Concluído</option>
                  <option value="Pendente de informações">Pendente de informações</option>
                  <option value="Em investigação">Em investigação</option>
                </select>

                <svg
                  class="select-icon h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            <div class="flex items-end gap-2 md:col-span-4 lg:col-span-1">
              <button
                type="button"
                class="h-11 w-full rounded-xl font-medium text-white transition"
                :style="{ backgroundColor: primaryColor }"
                @click="start"
                @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                @mouseleave="$event.target.style.backgroundColor = primaryColor"
              >
                Buscar
              </button>

              <button
                type="button"
                class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50"
                @click="limparFiltros"
              >
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <router-link
          to="/suporte/form"
          class="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition hover:shadow-md sm:w-auto"
        >
          <svg
            class="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
            />
          </svg>
          <span class="text-sm font-medium text-gray-700">Abrir ticket</span>
        </router-link>
      </div>

      <div
        v-if="avisos.length > 0"
        class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      >
        <div class="border-b border-gray-100 px-6 py-5">
          <h2 class="text-lg font-semibold text-gray-900">Avisos do sistema</h2>
          <p class="mt-1 text-sm text-gray-500">
            Comunicados importantes disponíveis no momento.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="aviso in avisos"
            :key="aviso._id"
            class="rounded-2xl border px-4 py-4 shadow-sm"
            :class="categoriaAvisoValidate(aviso)"
          >
            <div class="flex items-start justify-between gap-3">
              <h3 class="text-sm font-semibold text-gray-800">
                {{ aviso.titulo }}
              </h3>
              <span class="whitespace-nowrap text-xs text-gray-500">
                {{ moment(aviso.dataInicio).format("DD/MM HH:mm") }}
              </span>
            </div>

            <p class="mt-2 text-sm text-gray-600">
              {{ aviso.descricao }}
            </p>
          </div>
        </div>
      </div>

      <div class="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Número</th>
                <th class="px-6 py-4 text-left font-medium">Ticket Aberto</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
                <th class="px-6 py-4 text-center font-medium">Prioridade</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="ticket in list"
                :key="ticket._id"
                class="border-b border-gray-100 transition hover:bg-slate-50"
              >
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                      {{ ticket.numero }}
                    </div>

                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">
                        Ticket #{{ ticket.numero }}
                      </span>
                      <span class="text-xs text-gray-400">Solicitação aberta</span>
                    </div>
                  </div>
                </td>

                <td class="px-6 font-medium text-gray-700">
                  {{ ticket.titulo || "-" }}
                </td>

                <td class="px-6 text-center">
                  <span
                    class="rounded-full px-3 py-1 text-xs font-medium"
                    :class="statusValidate(ticket)"
                  >
                    {{ ticket.status }}
                  </span>
                </td>

                <td class="px-6 text-center">
                  <span
                    class="rounded-full px-3 py-1 text-xs font-medium"
                    :class="prioridadeValidate(ticket)"
                  >
                    {{ ticket.prioridade }}
                  </span>
                </td>

                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <router-link
                      :to="`/suporte/form/${ticket._id}`"
                      class="rounded-lg px-4 py-2 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                    >
                      Detalhes
                    </router-link>
                  </div>
                </td>
              </tr>

              <tr v-if="list.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-sm text-gray-400">
                  Nenhum ticket encontrado com os filtros atuais.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  data() {
    return {
      moment,
      status: "",
      list: [],
      avisos: []
    };
  },

  methods: {
    async start() {
      const tickets = await this.$http.post("/v1/ticket/list", {
        status: this.status
      });

      const avisos = await this.$http.get("/v1/notify");

      this.list = tickets.data.ticket || [];
      this.avisos = avisos.data.data || [];
    },

    limparFiltros() {
      this.status = "";
      this.start();
    },

    statusValidate(ticket) {
      if (ticket.status === "Em aberto") return "bg-green-100 text-green-700";
      if (ticket.status === "Em investigação") return "bg-yellow-100 text-yellow-700";
      if (ticket.status === "Pendente de informações") return "bg-red-100 text-red-700";
      if (ticket.status === "Concluído") return "bg-blue-100 text-blue-700";
      return "bg-gray-100 text-gray-700";
    },

    prioridadeValidate(ticket) {
      if (ticket.prioridade === "Alta") return "bg-red-100 text-red-700";
      if (ticket.prioridade === "Média") return "bg-yellow-100 text-yellow-700";
      if (ticket.prioridade === "Baixa") return "bg-gray-100 text-gray-700";
      return "bg-gray-100 text-gray-700";
    },

    categoriaAvisoValidate(aviso) {
      if (aviso.nivel === "Alta") return "border-yellow-200 bg-yellow-50";
      if (aviso.nivel === "Média") return "border-yellow-100 bg-yellow-50";
      if (aviso.nivel === "Baixa") return "border-gray-200 bg-slate-50";
      return "border-gray-200 bg-slate-50";
    }
  },

  async beforeMount() {
    await this.start();
  }
};
</script>

<style scoped>
.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

#status-select::-ms-expand {
  display: none;
}

#status-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>